import React, { useState, useEffect } from "react";
import moment from "moment";
import { Box, Typography, makeStyles, Paper } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { Spinner } from "react-bootstrap";
import styled from "styled-components";
import { useParams } from "react-router";
import { getTimelinePatientById } from "../../../actions/radminUploads";

const Heading = styled.p`
  font-weight: 700;
  font-size: 24px;
  line-height: 28.8px;
  font-family: "Roboto Condensed";
  color: #0c2945;
  margin-bottom: 62px;
`;

const MIN_EVENT_VISUAL_WIDTH = 200;
const EVENT_PADDING_PX = 20;

const VERTICAL_LINE_LENGTH = 53;
const TOP_VERTICAL_LINE = 13;
const CIRCLE_DIAMETER = 16;
const CIRCLE_RADIUS = CIRCLE_DIAMETER / 2;

function formatMonthLabel(label) {
  return label.replace(/_/g, " ");
}

const useStyles = makeStyles((theme) => ({
  timelineContainer: {
    position: "relative",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(6),
    paddingTop: theme.spacing(2),
    paddingLeft: "0px",
    height: "auto",
    width: "100%",
    overflow: "visible",
  },
  monthHeader: {
    position: "absolute",
    top: -theme.spacing(15),
    transform: "translateX(-50%)",
    color: "#34495E",
    fontWeight: "bold",
    minWidth: "120px",
    textAlign: "center",
    whiteSpace: "nowrap",
    fontSize: "0.7rem",
    padding: "2px 8px",
    borderRadius: "4px",
    zIndex: 2,
  },
  firstMonthHeader: {
    position: "absolute",
    top: -theme.spacing(15),
    transform: "translateX(-50%)",
    color: "#34495E",
    fontWeight: "bold",
    fontSize: "0.7rem",
    textAlign: "center",
    whiteSpace: "nowrap",
    padding: "2px 8px",
    borderRadius: "4px",
    zIndex: 2,
  },
  monthDividerLine: {
    width: 2,
    backgroundColor: "#E0E0E0",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 0,
  },
  eventContainer: {
    position: "absolute",
    transform: "translateX(-50%)",
    textAlign: "center",
    width: `${MIN_EVENT_VISUAL_WIDTH}px`,
  },
  circle: {
    width: CIRCLE_DIAMETER,
    height: CIRCLE_DIAMETER,
    borderRadius: "50%",
    border: "3px solid #2196f3",
    backgroundColor: "#fff",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1,
  },
  horizontalLine: {
    position: "absolute",
    height: "2px",
    backgroundColor: "#e0e0e0",
    top: "0px",
    zIndex: 0,
  },
  line: {
    width: 2,
    backgroundColor: "#2196f3",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 0,
  },
  topLabel: {
    bottom: "100%",
    marginBottom: theme.spacing(9),
    left: theme.spacing(4),
  },
  bottomLabel: {
    top: "100%",
    marginTop: theme.spacing(6),
  },
  idChip: {
    backgroundColor: "#e0f7fa",
    padding: "2px 8px",
    marginTop: theme.spacing(0.5),
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
  },
  hoverCard: {
    position: "absolute",
    zIndex: 10,
    padding: theme.spacing(1.5),
    minWidth: "300px",
    maxWidth: "700px",
    backgroundColor: "#F0F8FF",
    borderRadius: "8px",
    opacity: 0,
    transition: "opacity 0.3s ease-in-out",
    "&:hover": {
      opacity: 1,
    },
  },
  hoverCardVisible: {
    // opacity: 1,
  },
  floatingMonthLabel: {
    position: "sticky",
    top: 0,
    left: 0,
    zIndex: 1000,
    backgroundColor: "white",
    padding: "6px 12px",
    fontWeight: 600,
    color: "#34495E",
    border: "1px solid #ccc",
    width: "fit-content",
    marginBottom: "8px",
  },
}));

const Timeline = ({ id, patientId }) => {
  const classes = useStyles();
  const [hoveredEventKey, setHoveredEventKey] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleMonth, setVisibleMonth] = useState("");

  const dispatch = useDispatch();


  const BASE_MONTH_WIDTH_PX = 150;

  const generateUniqueEventKey = (event, globalIndex) => {
    return `${event.event_id || "no-id"}-${moment(
      event.event_time,
      "MM-DD-YYYY hh:mm:ss A"
    ).valueOf()}-${globalIndex}`;
  };

  useEffect(() => {
    const loadTimelineData = async () => {
      try {
        setLoading(true);


        await dispatch(getTimelinePatientById(id || patientId))
          .then((res) => {
            setTimelineData(Array.isArray(res) ? res : []);
          })
          .catch((err) => {
            console.error("Error fetching timeline data:", err);
            setError(err.message || "Failed to load timeline data.");
          });
        setError(null);
      } catch (err) {
        console.error("Caught error in loadTimelineData:", err);
        setError("Failed to load timeline data.");
        setTimelineData(null);
      } finally {
        setLoading(false);
      }
    };

    loadTimelineData();
  }, [dispatch, id, patientId]);


  if (loading) {
    return (
      <>
        <Box
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spinner animation="border" role="status" />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100%",
          marginTop: "12px",
          paddingTop: "81px",
          marginLeft: "40px",
        }}
      >
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  const validEvents =
    timelineData &&
    Array.isArray(timelineData) &&
    timelineData.length > 0 &&
    timelineData.filter((event) => {
      const isValid = moment(
        event.event_time,
        "MM-DD-YYYY hh:mm:ss A",
        true
      ).isValid();
      if (!isValid) {
        console.warn(
          `Invalid date format for event: "${event.event_time}" from event ID: "${event.event_id}". This event will be skipped.`
        );

      }
      return isValid;
    });

  if (!validEvents || validEvents.length === 0) {
    return (
      <Box
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <Typography
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          variant="body1"
          color="textSecondary"
        >

          No valid events to display.
        </Typography>
      </Box>
    );
  }

  const sortedEvents =
    validEvents &&
    validEvents.sort((a, b) => {
      const dateA = moment(a.event_time, "MM-DD-YYYY hh:mm:ss A").valueOf();
      const dateB = moment(b.event_time, "MM-DD-YYYY hh:mm:ss A").valueOf();
      if (dateA === dateB) {
        return String(a.event_id || "").localeCompare(String(b.event_id || ""));
      }
      return dateB - dateA;
    });


  const dates = sortedEvents.map((event) =>
    moment(event.event_time, "MM-DD-YYYY hh:mm:ss A").toDate()
  );

  const timelineStartDate = moment(Math.min(...dates)).startOf("month");
  const timelineEndDate = moment(Math.max(...dates)).endOf("month");


  const monthList = [];
  let currentDate = timelineStartDate.clone();

  while (currentDate.isSameOrBefore(timelineEndDate)) {
    const monthKey = currentDate.format("MMMM_YYYY");
    monthList.push({
      monthKey,
      startDate: currentDate.toDate(),
      endDate: currentDate
        .clone()
        .endOf("month")
        .toDate(),
      width: BASE_MONTH_WIDTH_PX,
      events: [],
    });
    currentDate.add(1, "month");
  }

  sortedEvents.forEach((event) => {
    const eventMoment = moment(event.event_time, "MM-DD-YYYY hh:mm:ss A");
    const eventMonthKey = eventMoment.format("MMMM_YYYY");
    const targetMonth = monthList.find((m) => m.monthKey === eventMonthKey);
    if (targetMonth) {
      targetMonth.events.push(event);
    } else {
      console.warn(
        `Event ${event.event_id} date ${event.event_time} is outside the defined month range.`
      );
    }
  });

  monthList.forEach((month) => {
    month.events.sort((a, b) => {
      const dateA = moment(a.event_time, "MM-DD-YYYY hh:mm:ss A").valueOf();
      const dateB = moment(b.event_time, "MM-DD-YYYY hh:mm:ss A").valueOf();
      if (dateA === dateB) {
        return String(a.event_id || "").localeCompare(String(b.event_id || ""));
      }
      return dateB - dateA;
    });
  });

  monthList.sort((a, b) => {
    const monthA = moment(a.monthKey, "MMMM_YYYY");
    const monthB = moment(b.monthKey, "MMMM_YYYY");
    return monthB.valueOf() - monthA.valueOf();
  });

  let cumulativeWidth = 0;
  monthList.forEach((month) => {
    const numEventsInMonth = month.events.length;
    const requiredEventsPx =
      numEventsInMonth > 0
        ? numEventsInMonth * (MIN_EVENT_VISUAL_WIDTH + EVENT_PADDING_PX) +
          EVENT_PADDING_PX
        : 0;


    month.width = Math.max(BASE_MONTH_WIDTH_PX, requiredEventsPx);

    month.startPx = cumulativeWidth;
    cumulativeWidth += month.width;
    month.endPx = cumulativeWidth;
  });

  const totalPixelWidth = cumulativeWidth;

  const adjustedPositions = new Map();
  let globalEventIndex = 0;

  monthList.forEach((month) => {
    let currentXInMonth = month.startPx + EVENT_PADDING_PX / 2;

    month.events.forEach((event) => {
      const uniqueKey = generateUniqueEventKey(event, globalEventIndex);
      const eventCenterPx = currentXInMonth + MIN_EVENT_VISUAL_WIDTH / 2;

      adjustedPositions.set(uniqueKey, {
        position: (eventCenterPx / totalPixelWidth) * 100,
        isTop: globalEventIndex % 2 === 0,
      });

      currentXInMonth += MIN_EVENT_VISUAL_WIDTH + EVENT_PADDING_PX;
      globalEventIndex++;
    });
  });

  const monthHeaders = monthList.map((month) => ({
    month: moment(month.monthKey).format("MMM YYYY") ,
    startPx: month.startPx,
    position: (month.startPx / totalPixelWidth) * 100,
  }));

  const getMonthFromDate = (dateString) => {
    const date = moment(dateString, "MM-DD-YYYY hh:mm:ss A");
    return date.isValid() ? date.format("MMM YYYY") : "Invalid Date";
  };

  return (
    <>
      <Box
        style={{
          width: "100%",
          height: "285px",
          overflow: "auto",
          marginTop: "12px",
          paddingTop: "81px",
          marginLeft: "40px",
          position: "relative",
        }}
      >
        <Box
          className={classes.timelineContainer}
          style={{
            width: `${totalPixelWidth}px`,
            minWidth: "100%",
          }}
        >
          <div
            className={classes.horizontalLine}
            style={{
              left: "0px",
              width: `${totalPixelWidth}px`,
            }}
          />

          {monthHeaders.map((header, index) => (
            <React.Fragment key={header.month}>
              <Typography
                variant="subtitle1"
                className={
                  index === 0 ? classes.firstMonthHeader : classes.monthHeader
                }
                style={{
                  // left: `${header.position}%`,
                  left: `${header.startPx}px`,
                  transform: index === 0 && "translateX(0%)",
                  paddingLeft: "10px",

                }}
              >
                {formatMonthLabel(header.month)}
              </Typography>
              {index < monthList.length - 1 && (
                <div
                  className={classes.monthDividerLine}
                  style={{
                    left: `${(monthList[index + 1].startPx / totalPixelWidth) *
                      100}%`,
                    top: -88,
                    height: 89,
                  }}
                />
              )}
              {index === monthList.length - 1 && (
                <div
                  className={classes.monthDividerLine}
                  style={{
                    left: `100%`,
                    top: -88,
                    height: 89,
                  }}
                />
              )}
            </React.Fragment>
          ))}

          {sortedEvents.map((event, eventMapIndex) => {
            const uniqueKey = generateUniqueEventKey(event, eventMapIndex);
            const eventPositionData = adjustedPositions.get(uniqueKey);

            if (!eventPositionData) {
              console.warn(
                `Position data missing for unique key: ${uniqueKey}. Skipping render.`
              );
              return null;
            }
            const { position, isTop } = eventPositionData;

            const circleTop = isTop
              ? -(VERTICAL_LINE_LENGTH + TOP_VERTICAL_LINE + CIRCLE_RADIUS)
              : VERTICAL_LINE_LENGTH - TOP_VERTICAL_LINE - CIRCLE_RADIUS;

            const lineTop = isTop
              ? -(VERTICAL_LINE_LENGTH + TOP_VERTICAL_LINE)
              : -15;

            const lineHeight = VERTICAL_LINE_LENGTH;

            return (
              <Box
                key={uniqueKey}
                className={classes.eventContainer}
                style={{ left: `${position}%` }}
                onMouseEnter={() => setHoveredEventKey(uniqueKey)}
                onMouseLeave={() => setHoveredEventKey(null)}
              >
                {isTop ? (
                  <>
                    <Box className={classes.topLabel} position="absolute">
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        style={{ width: "100%" }}
                      >
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ textAlign: "center", fontSize: "0.6rem" }}
                        >
                          {event.event_time}

                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="primary"
                        style={{
                          color: "#2196f3",
                          fontSize: "0.6rem",
                          textAlign: "center",
                        }}
                      >
                        {event.event_name}
                      </Typography>
                    </Box>

                    {hoveredEventKey === uniqueKey && (
                      <Paper
                        elevation={3}
                        className={`${classes.hoverCard} ${
                          hoveredEventKey === uniqueKey
                            ? classes.hoverCardVisible
                            : ""
                        }`}
                        style={{
                          top: event?.extra_details?.accession_number
                            ? -110
                            : -100,

                          left: eventMapIndex === 0 ? "75%" : "50%",
                          transform: "translateX(-50%)",
                          textAlign: "left",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{
                            fontSize: "0.6rem",
                            marginBottom:
                              !event?.extra_details?.accession_number && "-9px",
                          }}
                        >
                          ID: {event.event_id}
                        </Typography>
                        {event?.extra_details?.accession_number && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{
                              fontSize: "0.6rem",
                              marginBottom: "-12px",
                            }}
                          >
                            Accession number:{" "}
                            {event?.extra_details?.accession_number}
                          </Typography>
                        )}

                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ fontSize: "0.6rem" }}
                        >
                          Time: {event.event_time}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          style={{
                            color: "rgb(33, 150, 243)",
                            fontSize: "0.6rem",
                          }}
                        >
                          {event.event_name}
                        </Typography>
                      </Paper>
                     )}
                   
                      <div
                        style={{
                          // top: event?.extra_details?.accession_number
                          //   ? -114
                          //   : -100,

                          marginTop: "-128px",
                          fontSize: "0.6rem",
                          fontWeight: 'bold',
                          // left: eventMapIndex === 0 ? "75%" : "50%",
                          // transform: "translateX(-50%)",
                        }}
                      >
                         {getMonthFromDate(event.event_time)}
                      </div>

                  </>
                ) : (
                  <>
                    <Box
                      className={classes.bottomLabel}
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography
                        variant="body2"
                        color="primary"
                        style={{
                          color: "#2196f3",
                          fontSize: "0.6rem",
                          textAlign: "center",
                        }}
                      >
                        {event.event_name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ fontSize: "0.6rem" }}
                      >
                        {event.event_time}

                      </Typography>
                    </Box>
                    {hoveredEventKey === uniqueKey && (
                      <Paper
                        elevation={3}
                        className={`${classes.hoverCard} ${
                          hoveredEventKey === uniqueKey
                            ? classes.hoverCardVisible
                            : ""
                        }`}
                        style={{
                          bottom: event?.extra_details?.accession_number
                            ? 0
                            : 14,
                          left: "50%",
                          transform: "translateX(-50%)",
                          textAlign: "left",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{
                            fontSize: "0.6rem",
                            marginBottom:
                              !event?.extra_details?.accession_number && "-9px",
                          }}
                        >
                          ID: {event.event_id}
                        </Typography>
                        {event?.extra_details?.accession_number && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{
                              fontSize: "0.6rem",
                              marginBottom: "-12px",
                            }}
                          >
                            Accession number:{" "}
                            {event?.extra_details?.accession_number}
                          </Typography>
                        )}


                        <Typography
                          variant="caption"
                          color="textSecondary"
                          style={{ fontSize: "0.6rem" }}
                        >
                          Time: {event.event_time}

                        </Typography>
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          style={{
                            color: "rgb(33, 150, 243)",
                            fontSize: "0.6rem",
                          }}
                        >
                          {event.event_name}
                        </Typography>
                      </Paper>
                    )}

                    {eventMapIndex !== 0 && (
                      <div
                        style={{
                          marginBottom: "-23px",
                          marginTop: "-2px",
                          fontSize: "0.6rem",
                          fontWeight: 'bold'
                        }}
                      >
                      {getMonthFromDate(event.event_time)}
                      </div>
                     )} 
                  </>
                )}
                <div className={classes.circle} style={{ top: circleTop }} />
                <div
                  className={classes.line}
                  style={{ height: lineHeight, top: lineTop }}
                />
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
};

export default Timeline;

